import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore } from '@lib/store';
import { useAccountFlow, useCurrencySymbol } from '@shared/hooks';
import { AmountUtils, cn, FormatUtils, PaletteUtils } from '@shared/utils';
import type { AccountResponse } from '@shared/api/rest/accounts';
import { ColorsEnum, Icon, Text } from '@shared/widgets';
import CardIcon from '@assets/icons/card.svg';
import styles from '../../styles/accountCardTile.module.scss';

interface AccountCardSmallTileProps {
  className?: string;
  account: AccountResponse;
}

export const AccountCardSmallTile = (props: AccountCardSmallTileProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const { t } = useTranslation('accounts');

  const accountFlow = useAccountFlow(profileFeatureStore.data?.accountFlows, props.account.id);
  const symbol = useCurrencySymbol(props.account.currency);
  const progress = useMemo(() => {
    if (props.account.balance) return props.account.balance / (props.account.creditLimit / 100);
    return 0;
  }, [props.account]);

  if (accountFlow?.view === false) return null;
  return (
    <div
      className={cn(
        'flex flex-col bg-white transition-shadow duration-200 dark:bg-soft-black',
        'hover:shadow-lg dark:hover:shadow-lg-dark',
        props.className,
      )}
    >
      <div className='flex justify-between'>
        <div className='ml-5 mt-5 flex flex-col'>
          <Text size='3xl'>{AmountUtils.transformToAmountWithCurrency(props.account.balance, props.account.currency)}</Text>
          <Text className='mt-2' weight='medium' size='xs' color={ColorsEnum.Secondary}>
            {props.account.name}
          </Text>
          <Text className='mt-1' weight='medium' size='xs' color={ColorsEnum.Secondary}>
            {props.account.productName}
          </Text>
          <Text className='mt-1' weight='medium'>
            {props.account.name}
          </Text>
        </div>
        <div
          className={cn(
            'relative flex h-32 w-36 flex-none flex-col items-center justify-end overflow-hidden rounded-tr-sm pb-2 pl-9 pr-1 pt-4 text-center',
            'before:bg-white before:dark:bg-soft-black',
            PaletteUtils.isInsigniaPalette && 'bg-soft-black before:border-r-primary dark:bg-slate',
            PaletteUtils.isVcfcPalette && 'bg-primary dark:before:border-r-ice',
            styles['account__top-right'],
          )}
        >
          <div
            className={cn(
              'mb-1',
              'before:content-[attr(data-icon)]',
              'after:content-[attr(data-icon)]',
              PaletteUtils.isInsigniaPalette && 'before:text-primary after:text-primary',
              PaletteUtils.isVcfcPalette && 'before:text-white after:text-white',
              styles['account__icon'],
            )}
            data-icon={symbol}
          />
          <Text weight='semibold' size='sm' color={ColorsEnum.White}>
            {t(`statuses.account.${props.account.status}`, { ns: 'common' })}
          </Text>
        </div>
      </div>
      <div className='flex flex-col px-5 py-4'>
        <div className='relative h-1 w-full rounded bg-light'>
          <div className='absolute left-0 h-full rounded bg-primary' style={{ width: `${progress}%` }} />
        </div>
        <div className='mt-2 flex justify-between'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            {t('card.tile.fields.spendingLimit')}
          </Text>
          <Text weight='medium' size='xs'>
            {`${AmountUtils.transformToAmountWithCurrency(props.account.balance, props.account.currency)} / ${AmountUtils.transformToAmountWithCurrency(props.account.creditLimit, props.account.currency)}`}
          </Text>
        </div>
        <div className='my-3 h-[1px] w-full bg-gray-black' />
        <div className='flex justify-between'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            {t('card.tile.fields.accountNumber')}
          </Text>
          <Text weight='medium' size='xs'>
            {FormatUtils.toSliceNumber(props.account.accountNumber)}
          </Text>
        </div>
        <div className='mt-2 flex justify-between'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            {t('card.tile.fields.cards')}
          </Text>
          <div className='flex gap-1'>
            <Text weight='medium' size='xs'>
              {props.account.cardsCount}
            </Text>
            <Icon icon={<CardIcon width='16' height='16' />} />
          </div>
        </div>
      </div>
    </div>
  );
};
